"use client";

import React, { useState } from "react";
import { cn } from "@/lib/utils";
import ButtonWithIcon from "@/components/ui/button-witn-icon";

export function ExpandableCardPreview({
  title,
  description,
  image,
  onClick,
  className,
}: {
  title: string;
  description: string;
  image: string;
  onClick?: () => void;
  className?: string;
}) {
  const [imgLoaded, setImgLoaded] = useState(false);

  return (
    <div
      role="button"
      tabIndex={0}
      onClick={onClick}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          onClick?.();
        }
      }}
      className={cn(
        "badtz-expandable-card group relative w-full overflow-hidden rounded-2xl border border-black/10 bg-white text-left shadow-sm transition-all duration-300 hover:-translate-y-1 hover:shadow-xl",
        className,
      )}
    >
      <div className="relative h-[240px] w-full overflow-hidden bg-neutral-200">
        {/* Skeleton shimmer visible until the image finishes loading */}
        <div
          className={cn(
            "absolute inset-0 skeleton-shimmer transition-opacity duration-300",
            imgLoaded ? "opacity-0 pointer-events-none" : "opacity-100",
          )}
          aria-hidden="true"
        />
        <img
          src={image}
          alt={title}
          className={cn(
            "absolute inset-0 h-full w-full object-cover object-center transition-all duration-500 group-hover:scale-[1.04]",
            imgLoaded ? "opacity-100" : "opacity-0",
          )}
          loading="lazy"
          decoding="async"
          onLoad={() => setImgLoaded(true)}
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/15 to-transparent opacity-80" />
      </div>

      <div className="relative flex h-[200px] flex-col p-6">
        {/* Skeleton lines while image hasn't loaded */}
        {!imgLoaded ? (
          <>
            <div className="skeleton-shimmer h-5 w-3/4 rounded-md mb-3" aria-hidden="true" />
            <div className="skeleton-shimmer h-3.5 w-full rounded-md mb-1.5" aria-hidden="true" />
            <div className="skeleton-shimmer h-3.5 w-5/6 rounded-md" aria-hidden="true" />
          </>
        ) : (
          <>
            <h3 className="line-clamp-1 text-lg font-semibold tracking-tight text-neutral-950">{title}</h3>
            <p className="mt-2 line-clamp-2 text-sm leading-relaxed text-neutral-600">{description}</p>
          </>
        )}
        <div className="mt-auto pt-4">
          <ButtonWithIcon />
        </div>
      </div>
    </div>
  );
}

